/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Host, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  CELL_TEXT_PRIMARY_CLASS,
  code3Formatter,
  falsyCellRenderer,
  rendererIconByName,
} from '@gh/common-internal-shared';
import {
  getDialogInitData,
  getDialogInput,
  MutableSelectionModel,
  NumberTypes,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
  GridRowEvent,
} from '@gh/core-ui-grid';
import { flatMap } from 'lodash';
import { getPbComponentItemFinderListItemId, isPbStructureSuitable } from '../../';
import { PbComponentItemFinderDialogConfig } from '../../resources/pb-component-item-finder-business.service';
import { PbComponentItemFinderListItem } from '../../resources/pb-component-item-finder.model';
import { AbstractPbComponentItemFinderViewComponent } from './abstract-pb-component-item-finder-view-component';
import { PbComponentItemFinderDialogComponent } from './pb-component-item-finder-dialog.component';
import { ColumnWidthPersistenceService } from '@gh/common-internal-shared';

@Component({
  standalone: false,
  selector: 'gh-pb-component-item-finder-full-view',
  templateUrl: 'pb-component-item-finder-full-view.component.html',
  styleUrls: ['pb-component-item-finder-full-view.component.scss'],
})
export class PbComponentItemFinderFullViewComponent extends AbstractPbComponentItemFinderViewComponent
  implements OnInit {

  input: PbComponentItemFinderDialogConfig;
  selectionModel: MutableSelectionModel<PbComponentItemFinderListItem>;
  structureGridDefinition: GridDefinition;

  currentStructures: PbComponentItemFinderListItem[];

  private structures: PbComponentItemFinderListItem[];
  private readonly STORAGE_KEY = 'pbComponentItemFinder:columnWidths';

  constructor(private gridDefinitionFactory: GridDefinitionFactory,
              private translationService: TranslationService,
              @Host() private finderDialog: PbComponentItemFinderDialogComponent,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private columnWidthService: ColumnWidthPersistenceService) {
    super();
  }

  @Input()
  set name(name: string) {
    const nameRe = name ? new RegExp(`${name.trim()}`, 'i') : void 0;
    this.currentStructures = nameRe ?
      this.structures.filter((structure) => isPbStructureSuitable(structure, nameRe)) : this.structures;
  }

  ngOnInit(): void {
    const { hierarchy } = getDialogInitData(this.dialogData);

    this.input = getDialogInput(this.dialogData);
    this.selectionModel = this.finderDialog.selectionModel;

    const structures = flatMap(hierarchy, ({ items }) => items || []);
    this.structures = structures;
    this.currentStructures = structures;

    this.structureGridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'code',
          width: this.columnWidthService.getColumnWidth(this.STORAGE_KEY, 'code', 170),
          headerName: this.translationService.get('lbl_code'),
          cellClass: CELL_TEXT_PRIMARY_CLASS,
          sort: 'asc',
          suppressSizeToFit: true,
        },
        { field: 'name', width: this.columnWidthService.getColumnWidth(this.STORAGE_KEY, 'name', 130), headerName: this.translationService.get('lbl_name') },
        {
          field: 'costCenterCode',
          width: this.columnWidthService.getColumnWidth(this.STORAGE_KEY, 'costCenterCode', 75),
          headerName: this.translationService.get('lbl_cc_number'),
          valueFormatter: code3Formatter,
          suppressSizeToFit: true,
        },
        {
          field: 'isActive',
          width: this.columnWidthService.getColumnWidth(this.STORAGE_KEY, 'isActive', 90),
          headerName: this.translationService.get('lbl_deleted'),
          cellRenderer: falsyCellRenderer(rendererIconByName('done')),
          suppressSizeToFit: true,
        },
        {
          field: 'uomCode',
          width: this.columnWidthService.getColumnWidth(this.STORAGE_KEY, 'uomCode', 140),
          headerName: this.translationService.get('lbl_uom_abbreviation'),
          suppressSizeToFit: true,
        },
        {
          field: 'price',
          width: this.columnWidthService.getColumnWidth(this.STORAGE_KEY, 'price', 110),
          headerName: this.translationService.get('lbl_price'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Price),
        },
        {
          field: 'origQuantity',
          width: this.columnWidthService.getColumnWidth(this.STORAGE_KEY, 'origQuantity', 110),
          headerName: this.translationService.get('lbl_orig_qty'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Quantity),
        },
        {
          field: 'origQuantityComponent',
          width: this.columnWidthService.getColumnWidth(this.STORAGE_KEY, 'origQuantityComponent', 140),
          headerName: this.translationService.get('lbl_cmp_qty'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Quantity),
        },
      ],
      {
        getRowId: (params) => String(getPbComponentItemFinderListItemId(params.data)),
        onColumnResized: this.columnWidthService.createColumnResizedHandler(this.STORAGE_KEY),
      });
  }

  onRowClicked(item: GridRowEvent<PbComponentItemFinderListItem>): void {
    if (this.isNumericSelector) {
      if (this.getCount(item.data) === 0) this.increaseCount(item.data);
      return;
    }
    super.onRowClicked(item);
  }
}
